
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { IonContent, IonPage, IonIcon, IonLoading, IonAlert, useIonToast } from '@ionic/react';
import {
  businessOutline,
  cameraOutline,
  compassOutline,
  keyOutline,
  lockClosedOutline,
  mailOutline,
  personOutline,
  callOutline,
  calendarOutline,
  shieldCheckmarkOutline,
  idCardOutline,
} from 'ionicons/icons';
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import defaultavatar from '../assets/avatardefault.svg';
import axios from '../axios';
import Header from '../components/Header';
import { AppContext } from '../providers/AppContext';
import { constant } from '../utils';

const Profile: React.FC = () => {
  const [present] = useIonToast();
  const [showLoading, setShowLoading] = useState(false);
  const [passToggle, setPassToggle] = useState(false);
  const { dispatch } = useContext(AppContext);
  const { t } = useTranslation();

  const [data, setData] = useState<any>({
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    full_name: '',
    gender: '',
    phone: '',
    birth_date: '',
    user_image: '',
    department: [],
    entity: '',
    role: '',
    access: '',
  });

  const getProfileData = () => {
    const config: any = {
      method: 'get',
      url: '/method/preflet_asset.api.get_profile_details',
      headers: { Authorization: `Basic ${localStorage.getItem('hyprism_login')}` },
    };
    try {
      axios(config).then((response) => {
        const d = response.data.data;
        setData({
          first_name: d.first_name,
          last_name: d.last_name,
          username: d.username,
          email: d.email,
          full_name: d.full_name,
          gender: d.gender,
          phone: d.phone,
          birth_date: d.birth_date,
          user_image: d.user_image,
          department: d.department,
          entity: d.entity,
          role: d.role,
          access: d?.access_level?.name1,
        });
      });
    } catch (error: any) {
      console.log(error);
    }
  };

  useEffect(() => {
    getProfileData();
  }, []);

  const takePicture = async () => {
    try {
      const image = await Camera.getPhoto({
        quality: 70,
        allowEditing: true,
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera,
      });
      uploadDp(image.webPath, image.format);
    } catch (e: any) {
      console.log('Error: ', e);
      if (e.toString().includes('cancelled')) {
        present({ message: t('Utils.cameraCancel'), color: 'danger', duration: 2000 });
      } else if (e.toString().includes('denied') || e.includes?.('access')) {
        present({ message: t('Utils.cameraErrorPermission'), color: 'danger', duration: 2000 });
      } else {
        present({ message: e, color: 'danger', duration: 2000 });
      }
    }
  };

  const uploadDp = async (imageUrl: any, format: any) => {
    const formData = new FormData();
    try {
      setShowLoading(true);
      const response = await fetch(imageUrl.toString());
      const blob = await response.blob();
      formData.append('file', new File([blob], `${data.username}-${Date.now()}.${format}`, { type: `image/${format}` }));
      formData.append('is_private', '0');
      formData.append('folder', 'Home');
      formData.append('doctype', 'User');

      const baseURL = localStorage.getItem('baseUrl') || process.env.REACT_APP_BASE_URL || 'https://admin.hyprism.com/api';
      const fetchUrl = `${baseURL.replace(/\/+$/, '')}/method/upload_file`;
      const uploadResponse = await fetch(fetchUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', Authorization: `Basic ${localStorage.getItem('hyprism_login')}` },
        body: formData,
      });

      if (!uploadResponse.ok && uploadResponse.status !== 200) throw new Error('Upload failed');
      const resData = await uploadResponse.json();
      hitLinkUpdateApi(resData?.message?.file_url);
    } catch (error: any) {
      console.log(error);
      setShowLoading(false);
    }
  };

  const hitLinkUpdateApi = async (imageUrl: any) => {
    const config: any = {
      method: 'post',
      url: '/method/preflet_asset.api.upload_profile_picture',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', Authorization: `Basic ${localStorage.getItem('hyprism_login')}` },
      data: JSON.stringify({ file_url: imageUrl }),
    };
    try {
      await axios(config);
      localStorage.setItem(constant.PROFILE_PHOTO, imageUrl);
      getProfileData();
    } catch (error: any) {
      console.log(error);
    } finally {
      setShowLoading(false);
    }
  };

  const updatePassword = async (passwords: any) => {
    const config: any = {
      method: 'post',
      url: '/method/preflet_asset.api.update_password',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', Authorization: `Basic ${localStorage.getItem('hyprism_login')}` },
      data: JSON.stringify({ old_password: passwords[0], new_password: passwords[1] }),
    };
    try {
      setShowLoading(true);
      await axios(config);
      present({ message: t(`Utils.passwordChanged`), color: 'success', duration: 2000 });
      dispatch({ type: 'logOut' });
    } catch (error: any) {
      present({ message: t(`Utils.` + error?.response?.data?.message?.data), color: 'danger', duration: 2000 });
    } finally {
      setShowLoading(false);
      setPassToggle(false);
    }
  };

  const getImg = () => {
    if (data.user_image) return `${localStorage.getItem('baseUrl')?.replace('/api', '')}${data.user_image}`;
    return defaultavatar;
  };

  const getInitials = () => {
    const fn = data.first_name?.[0] || '';
    const ln = data.last_name?.[0] || '';
    return (fn + ln).toUpperCase() || '?';
  };

  type InfoRowProps = { icon: string; label: string; value: string };
  const InfoRow: React.FC<InfoRowProps> = ({ icon, label, value }) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '14px',
      padding: '14px 20px', borderBottom: '1px solid rgba(29, 80, 141, 0.06)',
    }}>
      <div style={{
        width: '36px', height: '36px', borderRadius: '10px',
        background: 'rgba(29, 80, 141, 0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <IonIcon icon={icon} style={{ color: '#1d508d', fontSize: '17px' }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: '11px', color: '#9aa5b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</p>
        <p style={{ margin: '2px 0 0', fontSize: '14px', color: '#1a2a3e', fontWeight: 600, wordBreak: 'break-word' }}>{value || '—'}</p>
      </div>
    </div>
  );

  return (
    <IonPage>
      <Header enableBackButton='goBack' title={t('Header.Avatar.Profile')} />
      <IonContent style={{ '--background': '#f0f4f8' }}>
        <IonLoading isOpen={showLoading} onDidDismiss={() => setShowLoading(false)} message={t('Utils.pleasewait')} />

        {/* Hero Banner */}
        <div style={{
          background: 'linear-gradient(135deg, #1d508d 0%, #2a6db5 60%, #3b88d8 100%)',
          padding: '2.5rem 1.5rem 5rem',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Decorative circles */}
          <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '180px', height: '180px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
          <div style={{ position: 'absolute', bottom: '-20px', left: '-30px', width: '120px', height: '120px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />
          <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: 'center' }}>
            {data.role || t('Header.Avatar.Profile')}
          </p>
          <h2 style={{ margin: '4px 0 0', fontSize: '22px', fontWeight: 800, color: '#fff', textAlign: 'center', letterSpacing: '-0.01em' }}>
            {data.full_name || '—'}
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'rgba(255,255,255,0.65)', textAlign: 'center' }}>
            {data.email || ''}
          </p>
        </div>

        {/* Profile Avatar - overlap banner */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '-64px', position: 'relative', zIndex: 2 }}>
          <div style={{ position: 'relative' }}>
            <div style={{
              width: '120px', height: '120px', borderRadius: '50%',
              border: '4px solid #fff',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(29, 80, 141, 0.25)',
              background: '#dde8f5',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {data.user_image ? (
                <img src={getImg()} alt="profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span style={{ fontSize: '38px', fontWeight: 800, color: '#1d508d' }}>{getInitials()}</span>
              )}
            </div>
            <button
              onClick={takePicture}
              style={{
                position: 'absolute', bottom: '4px', right: '4px',
                width: '32px', height: '32px', borderRadius: '50%',
                background: 'linear-gradient(135deg, #1d508d, #2a6db5)',
                border: '2px solid #fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', boxShadow: '0 4px 10px rgba(29,80,141,0.35)',
              }}
            >
              <IonIcon icon={cameraOutline} style={{ color: '#fff', fontSize: '15px' }} />
            </button>
          </div>
        </div>

        {/* Change Password Button */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
          <button
            onClick={() => setPassToggle(true)}
            style={{
              background: 'linear-gradient(90deg, #1d508d, #2a6db5)',
              color: '#fff',
              border: 'none',
              borderRadius: '20px',
              padding: '8px 22px',
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 4px 14px rgba(29,80,141,0.2)',
            }}
          >
            <IonIcon icon={lockClosedOutline} style={{ fontSize: '14px' }} />
            Change Password
          </button>
        </div>

        {/* Account Details Card */}
        <div style={{ margin: '20px 16px 0', borderRadius: '20px', overflow: 'hidden', background: '#fff', boxShadow: '0 4px 20px rgba(29,80,141,0.07)' }}>
          <div style={{ padding: '14px 20px 10px', borderBottom: '1px solid rgba(29, 80, 141, 0.08)' }}>
            <p style={{ margin: 0, fontSize: '12px', fontWeight: 700, color: '#9aa5b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {t('Service.Detail')}
            </p>
          </div>
          <InfoRow icon={idCardOutline} label={t('Profile.ID')} value={data.username} />
          <InfoRow icon={mailOutline} label="Email" value={data.email} />
          <InfoRow icon={callOutline} label={t('Profile.C')} value={data.phone} />
          <InfoRow icon={calendarOutline} label={t('Profile.DOB')} value={data.birth_date} />
        </div>

        {/* Organization Card */}
        <div style={{ margin: '16px 16px 0', borderRadius: '20px', overflow: 'hidden', background: '#fff', boxShadow: '0 4px 20px rgba(29,80,141,0.07)' }}>
          <div style={{ padding: '14px 20px 10px', borderBottom: '1px solid rgba(29, 80, 141, 0.08)' }}>
            <p style={{ margin: 0, fontSize: '12px', fontWeight: 700, color: '#9aa5b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {t('Profile.Personal')}
            </p>
          </div>
          <InfoRow icon={compassOutline} label="Entity" value={data.entity} />
          <InfoRow
            icon={businessOutline}
            label="Departments"
            value={
              data.department?.length > 0
                ? data.department.length === 1 ? data.department[0] : data.department.join(' · ')
                : '—'
            }
          />
          <InfoRow icon={shieldCheckmarkOutline} label="Role" value={data.role} />
          <InfoRow icon={keyOutline} label="Access Level" value={data.access} />
        </div>

        <div style={{ height: '40px' }} />

        <IonAlert
          isOpen={passToggle}
          onDidDismiss={() => setPassToggle(false)}
          header="Change Password"
          message="Your password must be at least 10 characters with symbols, numbers, and mixed case letters."
          inputs={[
            { type: 'password', placeholder: 'Current Password' },
            { type: 'password', placeholder: 'New Password' },
          ]}
          buttons={[
            { text: 'Cancel', role: 'cancel' },
            {
              text: t('Utils.changePassword'),
              handler: (event) => { updatePassword(event); },
            },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default Profile;
